import classnames from 'classnames'

import Container from '~/components/atoms/Container/Container'
import Fixture from '~/components/atoms/Fixture/Fixture'
import { Col, ContainerTypes, Grid, Row } from '~/components/atoms/Grid'
import Image from '~/components/atoms/Image/Image'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import {
  componentType,
  componentTypes,
} from '~/components/blocks/ImageWithText/imageWithTextComponentTypes'
import { ComponentTheme } from '~/components/themes'
import { useMediaQuery } from '~/services/useMediaQuery'
import bp from '~/styles/breakpoints/breakpoints'

import type { PartnerProps } from '../PartnerCard/PartnerCard'
import { PartnerResultsCardDetails } from '../PartnerResultsCard/PartnerResultsCard'

import styles from './LargePartnerResultsCard.module.css'

export interface LargePartnerResultsCardProps {
  theme?: ComponentTheme
  flip?: boolean
  type?: componentType
  marginBottom?: boolean
  verticalPadding?: boolean
  noPaddingBottom?: boolean
  testID?: string
  moduleId?: string
  partner: PartnerProps
}

export default function LargePartnerResultsCard({
  partner,
  theme = ComponentTheme.primary,
  flip = false,
  type = componentType.singleColumn,
  verticalPadding,
  noPaddingBottom,
  testID,
  moduleId,
}: LargePartnerResultsCardProps) {
  const canFlip =
    type === componentType.hero
      ? // eslint-disable-next-line react-hooks/rules-of-hooks
        useMediaQuery(`(min-width: ${bp.bpMedium})`)
      : true

  const image = partner?.image

  const renderImageCol = () => {
    if (!image) return
    return (
      <>
        <div
          className={classnames(styles.imageContainer, styles[type])}
          data-testid={testID && `image-container-${testID}`}
        >
          <div className={styles.imageRatio}>
            <Image
              asset={image.asset}
              objectFit={image.objectFit}
              sizes="(max-width: 799px) 100vw,
                50vw"
              alt={image.alt}
            />
          </div>
          {image?.credit?.text && (
            <Text
              className={classnames(styles.imageCredit, {
                [styles[image.credit.theme]]: image.credit.theme,
              })}
              size={TextSizes.tertiaryTitleSml}
            >
              {image.credit.text}
            </Text>
          )}
        </div>
      </>
    )
  }

  const renderTextCol = () => {
    const isFullWidth = componentTypes[type].container === 'noMaxWidth'

    const handlePadding =
      [
        componentType.categoryImageFullWidth,
        componentType.categoryImageFullWidthMagazine,
      ].includes(type) && isFullWidth

    return (
      <div
        className={classnames(
          styles.details,
          styles[type],
          flip && styles[`${type}Flip`],
          handlePadding &&
            (flip
              ? styles.imageWithTextDetailsFlip
              : styles.imageWithTextDetails)
        )}
        data-testid={testID && `details-container-${testID}`}
      >
        <div className={styles[`${type}Inner`]}>
          <PartnerResultsCardDetails {...partner} theme="light" size="large" />
        </div>
      </div>
    )
  }

  const renderGrid = () => {
    return (
      <Grid variant={ContainerTypes.NoGutters}>
        <Row>
          <Col
            variant={
              flip
                ? componentTypes[type].styles[0]
                : componentTypes[type].styles[1]
            }
            cssClass={canFlip && flip && styles.order2}
          >
            {renderImageCol()}
          </Col>
          <Col
            variant={
              flip
                ? componentTypes[type].styles[1]
                : componentTypes[type].styles[0]
            }
            cssClass={
              type === componentType.twoColumn && !flip && styles.newspaper
            }
          >
            {renderTextCol()}
          </Col>
        </Row>
      </Grid>
    )
  }

  return (
    <Fixture
      theme={theme}
      verticalPadding={verticalPadding}
      noPaddingBottom={noPaddingBottom}
      className={`${styles.container} ${
        type === componentType.twoColumn && flip && styles.newspaper
      }`}
      id={moduleId}
    >
      <Container
        gutters={componentTypes[type].gutters}
        className={componentTypes[type].container}
      >
        {renderGrid()}
      </Container>
    </Fixture>
  )
}
